import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, API, apiReady, setSessionUid, setReadOnly } from "@/lib/api";

const TAB_KEY = "dp_session_owner";
// Tell the other tabs of this browser which account the shared session cookie now belongs to ("none" = logged out).
const announce = (uid) => { try { localStorage.setItem(TAB_KEY, JSON.stringify({ uid, t: Date.now() })); } catch { /* private mode */ } };

const AuthCtx = createContext(null);
const RtCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [switched, setSwitched] = useState(false);
  const [notice, setNotice] = useState("");
  const uidRef = useRef(null);
  const setUser = useCallback((v) => {
    setUserState((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      uidRef.current = next ? next.id : null;
      setSessionUid(uidRef.current);
      setReadOnly(next?.role === "viewer");
      return next;
    });
  }, []);
  const refresh = useCallback(async () => {
    // Server briefly unreachable (restart / deploy / Cloudflare): keep trying, never send the user to the login for that.
    await apiReady;
    for (let i = 0; ; i += 1) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        announce(data.id);
        return data;
      } catch (e) {
        if (e.response && e.response.status < 500) { setUser(false); return false; }
        if (i >= 4) { setNotice("Server momentaneamente non raggiungibile: riprova ad accedere tra qualche secondo"); setUser((u) => u || false); return false; }
        await new Promise((r) => setTimeout(r, Math.min(1000 * (i + 1), 5000)));
      }
    }
  }, [setUser]);
  useEffect(() => {
    refresh();
    // Really revoked/expired: requests and websocket stop (user=false unmounts everything), straight to the login page.
    const onExpired = () => {
      if (uidRef.current) setNotice("Sessione scaduta, accedi di nuovo");
      setUser(false);
    };
    const onSwitched = () => { if (uidRef.current) setSwitched(true); };
    const onMust = () => setUser((u) => (u ? { ...u, must_change_password: true } : u));
    const onStorage = (e) => {
      if (e.key !== TAB_KEY || !e.newValue || !uidRef.current) return;
      let uid;
      try { ({ uid } = JSON.parse(e.newValue)); } catch { return; }
      if (uid === "none") { setNotice("Sei uscito da questo browser in un'altra scheda"); setUser(false); } else if (uid !== uidRef.current) setSwitched(true);
    };
    window.addEventListener("auth:expired", onExpired);
    window.addEventListener("auth:switched", onSwitched);
    window.addEventListener("auth:mustchange", onMust);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("auth:expired", onExpired);
      window.removeEventListener("auth:switched", onSwitched);
      window.removeEventListener("auth:mustchange", onMust);
      window.removeEventListener("storage", onStorage);
    };
  }, [refresh, setUser]);
  const login = async (identifier, password, remember, area = "staff") => {
    const { data } = await api.post("/auth/login", { identifier, password, remember, area });
    setNotice("");
    setSwitched(false);
    setUser(data);
    announce(data.id);
    return data;
  };
  const logout = async (all = false) => {
    try {
      await api.post(all ? "/auth/logout-all" : "/auth/logout");
    } catch { /* the browser session is closed locally anyway */ } finally {
      setUser(false);
      announce("none");
    }
  };
  return <AuthCtx.Provider value={{ user, setUser, login, logout, refresh, switched, notice }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export function RealtimeProvider({ children }) {
  const { user, switched } = useAuth();
  const listeners = useRef(new Map());
  const lastTs = useRef(null);
  const [connected, setConnected] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const active = user && !user.must_change_password && !switched;
  const uid = active ? user.id : null;
  const role = active ? user.role : null;

  useEffect(() => {
    if (!uid) return;
    let ws, timer, ping, stop = false, step = 0, opened = false;
    const STEPS = [1000, 2000, 5000, 10000];
    const seen = new Set(), order = [];
    const dispatch = (msg) => {
      if (msg.event_id) {
        if (seen.has(msg.event_id)) return;
        seen.add(msg.event_id); order.push(msg.event_id);
        if (order.length > 3000) seen.delete(order.shift());
      }
      if (msg.ts) lastTs.current = msg.ts;
      if (msg.event === "notification.new") setUnreadNotifs((n) => n + 1);
      [msg.event, msg.alias].filter(Boolean).forEach((ev) => (listeners.current.get(ev) || []).forEach((fn) => fn(msg.data || {})));
    };
    const resync = async () => {
      if (lastTs.current) {
        try {
          const { data } = await api.get("/events", { params: { since: lastTs.current } });
          data.events.forEach((m) => dispatch(JSON.parse(m)));
        } catch { /* full resync below */ }
      }
      ["rt.resync", "work.updated", "MESSAGES_ACCOUNT_LIST_UPDATED"].forEach((ev) => (listeners.current.get(ev) || []).forEach((fn) => fn({ resync: true })));
      api.get("/notifications", { params: { unread: true, limit: 1 } }).then((r) => setUnreadNotifs(r.data.unread)).catch(() => {});
    };
    api.get("/notifications", { params: { unread: true, limit: 1 } }).then((r) => setUnreadNotifs(r.data.unread)).catch(() => {});
    // Safety net ONLY while the socket is down: replay the shared bus every 5 s (read-only, deduped by event_id).
    let cursor = null;
    const poll = async () => {
      if (ws && ws.readyState === 1) { cursor = new Date().toISOString(); return; }
      try {
        const since = cursor ? new Date(new Date(cursor).getTime() - 20000).toISOString() : new Date().toISOString();
        const { data } = await api.get("/events", { params: { since } });
        if (cursor) data.events.forEach((m) => dispatch(JSON.parse(m)));
        cursor = data.now;
      } catch { /* next tick */ }
    };
    poll();
    const pollTimer = setInterval(poll, 5000);
    const connect = () => {
      ws = new WebSocket(`${API.replace(/^http/, "ws")}/${role === "creator" ? "creator-area/ws" : "ws"}?uid=${encodeURIComponent(uid)}`);
      ws.onopen = () => {
        setConnected(true);
        step = 0;
        ping = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
        if (opened) resync();
        else lastTs.current = lastTs.current || new Date().toISOString();
        opened = true;
      };
      ws.onmessage = (e) => dispatch(JSON.parse(e.data));
      ws.onclose = async (e) => {
        setConnected(false);
        clearInterval(ping);
        if (stop) return;
        if (e.code === 4409) { window.dispatchEvent(new Event("auth:switched")); return; }
        if (e.code === 4401) {
          // Reconnect only with a renewed token; a real refusal ends the session (no reconnect loop).
          try { await api.get("/auth/me"); } catch (err) { if (err.response?.status === 401) return; }
          if (stop) return;
        }
        timer = setTimeout(connect, STEPS[Math.min(step, STEPS.length - 1)]);
        step += 1;
      };
    };
    connect();
    return () => {
      stop = true;
      clearInterval(pollTimer);
      clearTimeout(timer);
      clearInterval(ping);
      ws && ws.close();
    };
  }, [uid]);

  const subscribe = useCallback((event, fn) => {
    const set = listeners.current.get(event) || new Set();
    set.add(fn);
    listeners.current.set(event, set);
    return () => set.delete(fn);
  }, []);
  return <RtCtx.Provider value={{ connected, subscribe, unreadNotifs, setUnreadNotifs }}>{children}</RtCtx.Provider>;
}

export const useRealtime = () => useContext(RtCtx);

export function useEvent(event, fn) {
  const { subscribe } = useRealtime();
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => subscribe(event, (d) => ref.current(d)), [event, subscribe]);
}

// Same handler for several realtime events (e.g. every dialog-state change of a conversation).
export function useEvents(events, fn) {
  const { subscribe } = useRealtime();
  const ref = useRef(fn);
  ref.current = fn;
  const key = events.join("|");
  useEffect(() => {
    const offs = key.split("|").map((e) => subscribe(e, (d) => ref.current(d, e)));
    return () => offs.forEach((off) => off && off());
  }, [key, subscribe]);
}

const LIVE_EVENTS = ["SALE_CREATED", "SALE_UPDATED", "SALE_DELETED", "SALE_REASSIGNED", "STATISTICS_CHANGED", "work.updated", "rt.resync"];

// Reloads server-scoped aggregates on every sale/shift change and after a realtime reconnect (debounced).
export function useLive(fn, events = []) {
  const { subscribe } = useRealtime();
  const ref = useRef(fn);
  ref.current = fn;
  const key = events.join(",");
  useEffect(() => {
    let t;
    const run = () => { clearTimeout(t); t = setTimeout(() => ref.current(), 250); };
    const offs = [...new Set([...LIVE_EVENTS, ...events])].map((ev) => subscribe(ev, run));
    return () => { clearTimeout(t); offs.forEach((off) => off()); };
  }, [subscribe, key]); // eslint-disable-line
}
